'use client'

import type { GaugePayload } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface GaugeRendererProps {
  payload: GaugePayload
}

/**
 * Custom SVG arc gauge. Renders a semicircle with colored threshold bands
 * and a needle indicating the current value.
 */
export function GaugeRenderer({ payload }: GaugeRendererProps) {
  const { value, min, max, thresholds } = payload.data
  const options = payload.options || {}
  const range = max - min || 1
  const clampedValue = Math.min(Math.max(value, min), max)
  const normalizedValue = (clampedValue - min) / range

  // SVG dimensions for the semicircle
  const cx = 120
  const cy = 110
  const r = 90
  const startAngle = Math.PI

  function polarToCartesian(angle: number) {
    return {
      x: cx + r * Math.cos(angle),
      y: cy - r * Math.sin(angle),
    }
  }

  function describeArc(startPct: number, endPct: number) {
    const a1 = startAngle - startPct * Math.PI
    const a2 = startAngle - endPct * Math.PI
    const start = polarToCartesian(a1)
    const end = polarToCartesian(a2)
    const largeArc = endPct - startPct > 0.5 ? 1 : 0
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
  }

  // Build arcs from thresholds
  const arcs: { startPct: number; endPct: number; color: string; label?: string }[] = []
  let prevPct = 0
  for (const t of thresholds) {
    const tPct = Math.min((t.upTo - min) / range, 1)
    arcs.push({ startPct: prevPct, endPct: tPct, color: resolveColor(t.color), label: t.label })
    prevPct = tPct
  }

  // Needle angle
  const needleAngle = startAngle - normalizedValue * Math.PI
  const needleLen = r - 15
  const needleTip = {
    x: cx + needleLen * Math.cos(needleAngle),
    y: cy - needleLen * Math.sin(needleAngle),
  }

  // Format display value
  let displayValue: string
  if (options.format === 'percent') {
    displayValue = `${(value * 100).toFixed(1)}%`
  } else if (options.format === 'integer') {
    displayValue = Math.round(value).toString()
  } else {
    displayValue = value.toFixed(1)
  }
  if (options.unit) displayValue += ` ${options.unit}`

  // Find active threshold label
  const activeThreshold = thresholds.find(t => clampedValue <= t.upTo)

  return (
    <div className="flex flex-col items-center">
      <svg width={240} height={140} viewBox="0 0 240 140">
        {/* Background track */}
        <path d={describeArc(0, 1)} fill="none" stroke="hsl(var(--muted))" strokeWidth={16} strokeLinecap="round" />

        {/* Threshold arcs */}
        {arcs.map((arc, i) => (
          <path
            key={i}
            d={describeArc(arc.startPct, arc.endPct)}
            fill="none"
            stroke={arc.color}
            strokeWidth={16}
            strokeLinecap="butt"
          />
        ))}

        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleTip.x}
          y2={needleTip.y}
          stroke="hsl(var(--foreground))"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={5} fill="hsl(var(--foreground))" />
      </svg>

      {/* Value display */}
      <div className="mt-1 text-center">
        <p className="text-2xl font-bold tracking-tight">{displayValue}</p>
        {activeThreshold?.label && (
          <p className="text-sm text-muted-foreground">{activeThreshold.label}</p>
        )}
      </div>
    </div>
  )
}
